import { create } from 'zustand';
import { DateTime } from 'luxon';
import { ISO8601TupleType } from 'types/common.types';
import {
  OvertimeDashboardStore,
  OvertimeByPosition,
  OvertimeByTeamMemberType,
  OvertimeTrendPoint,
  OvertimeIndividual,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/store/overtimeDashboard.store.types';
import {
  TotalWeeklyOvertimeHoursResponse,
  WeeklyOvertimeRateResponse,
  WeeklyOvertimeByPositionResponse,
  WeeklyOvertimeByPositionResponseItem,
  WeeklyOvertimeByEmploymentStatusResponse,
  WeeklyOvertimeByEmploymentStatusResponseItem,
  WeeklyOvertimeDriverRowResponseItem,
  WeeklyOvertimeDriversResponse,
  WeeklyOvertimeTrendResponse,
  isSingleSchedulingCohort,
  buildSchedulingCohortQuery,
  buildPayPeriodThroughRangeQuery,
  buildWeeklyOvertimeTrendQuery,
  mapOvertimeTrendBuckets,
} from 'pages/ReportsPage/components/AnalyticsDashboards/overtimeMappers';
import {
  DEFAULT_OVERTIME_TREND_PERIODS,
  formatEmploymentStatusLabel,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/OvertimeDashboard.constants';
import { toNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/calloutMappers';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { unwrapDashboardRoot } from 'pages/ReportsPage/components/AnalyticsDashboards/unwrapDashboardRoot';
import { fetchDashboardPage } from 'pages/ReportsPage/components/AnalyticsDashboards/fetchDashboardPage';
import { hasDataLockRejection } from 'pages/ReportsPage/store/reportsDataHealth.store';
import { OVERTIME_DATA_LOCKED_STUB } from 'pages/ReportsPage/components/AnalyticsDashboards/OvertimeDashboard/store/overtimeDashboard.stub';
import {
  DASHBOARDS_OVERTIME_TOTAL_WEEKLY_OVERTIME_HOURS,
  DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_RATE,
  DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_BY_POSITION,
  DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_BY_EMPLOYMENT_STATUS,
  DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_TREND,
  DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_DRIVERS,
} from 'constants/endpoints';
import { devtoolsInNonProd } from 'store/zustand/index.utils';

const INITIAL_STATE = {
  totalOtHours: 0,
  totalOtHoursChangePrct: null as number | null,
  totalOtHoursError: false,
  otRate: 0,
  otRateChangePrct: null as number | null,
  otRateError: false,
  isCohortSelected: false,
  priorPeriodRange: null as ISO8601TupleType | null,
  overtimeByPosition: null as OvertimeByPosition[] | null,
  byPositionPage: 1,
  byPositionTotalPages: 1,
  byPositionLoadingMore: false,
  overtimeByTeamMemberType: null as OvertimeByTeamMemberType[] | null,
  overtimeTrend: null as OvertimeTrendPoint[] | null,
  trendPeriods: DEFAULT_OVERTIME_TREND_PERIODS,
  trendLoading: false,
  overtimeIndividuals: null as OvertimeIndividual[] | null,
  individualsPage: 1,
  individualsTotalPages: 1,
  individualsLoading: false,
  isLoading: false,
  error: null as string | null,
};

export function derivePriorPeriodRange(
  source: Nullable<{
    current_pay_period_start_date?: string;
    current_pay_period_end_date?: string;
  }>,
): ISO8601TupleType | null {
  if (!source?.current_pay_period_start_date || !source?.current_pay_period_end_date) return null;
  const start = DateTime.fromISO(source.current_pay_period_start_date).startOf('day');
  const end = DateTime.fromISO(source.current_pay_period_end_date).startOf('day');
  if (!start.isValid || !end.isValid || end < start) return null;
  const periodDays = Math.round(end.diff(start, 'days').days) + 1;
  return [
    start.minus({ days: periodDays }).toString(),
    start.minus({ days: 1 }).endOf('day').toString(),
  ];
}

function mapOvertimeByPosition(rows: WeeklyOvertimeByPositionResponseItem[]): OvertimeByPosition[] {
  return rows.map((item) => ({
    position: item.position_name,
    overtimeHours: toNumber(item.total_overtime_hours),
  }));
}

function mapOvertimeByEmploymentStatus(
  rows: WeeklyOvertimeByEmploymentStatusResponseItem[],
): OvertimeByTeamMemberType[] {
  const totalHours = rows.reduce((sum, item) => sum + toNumber(item.total_overtime_hours), 0);
  return rows.map((item) => {
    const overtimeHours = toNumber(item.total_overtime_hours);
    return {
      type: formatEmploymentStatusLabel(item.employment_status),
      overtimeHours,
      percentage: totalHours > 0 ? Math.round((overtimeHours / totalHours) * 10000) / 100 : 0,
    };
  });
}

export function mapWeeklyOvertimeDrivers(
  rows: WeeklyOvertimeDriverRowResponseItem[],
): OvertimeIndividual[] {
  return rows.map((row) => ({
    teamMemberId: row.jane_employee_number || '—',
    teamMemberName: row.jane_name,
    teamMemberType: row.jane_employment_status_label
      ? formatEmploymentStatusLabel(row.jane_employment_status_label)
      : '—',
    overtimeHours: toNumber(row.total_overtime_hours),
  }));
}

interface TrendBaseParams {
  dateRange: [string, string];
  locationIds: string[] | null;
  positions: string[] | null;
  employmentTypes: string[];
}

let fetchId = 0;
let byPositionBaseQuery: Record<string, unknown> | null = null;
let driversFetchId = 0;
let driversBaseQuery: Nullable<Record<string, unknown>> = null;
let trendFetchId = 0;
let trendBaseParams: Nullable<TrendBaseParams> = null;

export const overtimeDashboardStore = create<OvertimeDashboardStore>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchData: (dateRange, locationIds, positions, employmentTypes) => {
      const currentFetchId = ++fetchId;
      const isCohortSelected = isSingleSchedulingCohort(locationIds, positions);

      set({
        isLoading: true,
        error: null,
        totalOtHoursError: false,
        otRateError: false,
        isCohortSelected,
      });

      const cohortQuery = isCohortSelected
        ? buildSchedulingCohortQuery(dateRange, locationIds, positions, employmentTypes)
        : null;

      const totalOtHoursRequest = cohortQuery
        ? createDashboardsRequest<TotalWeeklyOvertimeHoursResponse>({
            url: DASHBOARDS_OVERTIME_TOTAL_WEEKLY_OVERTIME_HOURS(),
            method: 'GET',
            query: cohortQuery,
          })
        : Promise.resolve(null);

      const otRateRequest = cohortQuery
        ? createDashboardsRequest<WeeklyOvertimeRateResponse>({
            url: DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_RATE(),
            method: 'GET',
            query: cohortQuery,
          })
        : Promise.resolve(null);

      const payPeriodRangeQuery = buildPayPeriodThroughRangeQuery(
        dateRange,
        locationIds,
        positions,
        employmentTypes,
      );

      const byPositionQuery = buildPayPeriodThroughRangeQuery(
        dateRange,
        locationIds,
        null,
        employmentTypes,
      );
      byPositionBaseQuery = byPositionQuery;

      const overtimeByPositionRequest = fetchDashboardPage<
        WeeklyOvertimeByPositionResponse,
        WeeklyOvertimeByPositionResponseItem
      >({
        url: DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_BY_POSITION(),
        query: byPositionQuery,
        page: 1,
        selectItems: (body) => body.weekly_overtime_by_position ?? [],
      });

      const overtimeByEmploymentStatusRequest =
        createDashboardsRequest<WeeklyOvertimeByEmploymentStatusResponse>({
          url: DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_BY_EMPLOYMENT_STATUS(),
          method: 'GET',
          query: payPeriodRangeQuery,
        });

      driversBaseQuery = payPeriodRangeQuery;
      driversFetchId++;

      const driversRequest = createDashboardsRequest<WeeklyOvertimeDriversResponse>({
        url: DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_DRIVERS(),
        method: 'GET',
        query: { ...payPeriodRangeQuery, page: 1 },
      });

      const trendPeriods = get().trendPeriods;
      trendBaseParams = { dateRange, locationIds, positions, employmentTypes };
      trendFetchId++;

      const trendRequest = cohortQuery
        ? createDashboardsRequest<WeeklyOvertimeTrendResponse>({
            url: DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_TREND(),
            method: 'GET',
            query: buildWeeklyOvertimeTrendQuery(
              dateRange,
              locationIds,
              positions,
              employmentTypes,
              trendPeriods,
            ),
          })
        : Promise.resolve(null);

      Promise.allSettled([
        totalOtHoursRequest,
        otRateRequest,
        overtimeByPositionRequest,
        overtimeByEmploymentStatusRequest,
        trendRequest,
        driversRequest,
      ]).then((results) => {
        const isDataLocked = hasDataLockRejection(results);
        if (currentFetchId !== fetchId) return;
        if (isDataLocked) {
          set({ ...INITIAL_STATE, ...OVERTIME_DATA_LOCKED_STUB, isLoading: false });
          return;
        }
        const [
          totalResult,
          rateResult,
          byPositionResult,
          byStatusResult,
          trendResult,
          driversResult,
        ] = results;

        const totalData = unwrapDashboardRoot<TotalWeeklyOvertimeHoursResponse>(
          totalResult.status === 'fulfilled' ? totalResult.value?.data ?? null : null,
          'total_weekly_overtime_hours',
        );
        const rateData = unwrapDashboardRoot<WeeklyOvertimeRateResponse>(
          rateResult.status === 'fulfilled' ? rateResult.value?.data ?? null : null,
          'weekly_overtime_rate',
        );
        const byPositionData =
          byPositionResult.status === 'fulfilled' ? byPositionResult.value : null;
        const byStatusData =
          byStatusResult.status === 'fulfilled' ? byStatusResult.value.data : null;
        const trendData =
          trendResult.status === 'fulfilled' ? trendResult.value?.data ?? null : null;
        const driversData = driversResult.status === 'fulfilled' ? driversResult.value.data : null;

        set((state) => ({
          ...state,
          totalOtHours: toNumber(totalData?.current_period_total_overtime),
          totalOtHoursChangePrct:
            totalData?.change_prct == null ? null : toNumber(totalData.change_prct),
          totalOtHoursError: totalResult.status === 'rejected',
          priorPeriodRange: derivePriorPeriodRange(totalData ?? rateData),
          otRate: toNumber(rateData?.current_period_overtime_rate),
          otRateChangePrct: rateData?.change_prct == null ? null : toNumber(rateData.change_prct),
          otRateError: rateResult.status === 'rejected',
          overtimeByPosition: byPositionData ? mapOvertimeByPosition(byPositionData.items) : null,
          byPositionPage: byPositionData?.meta?.current_page ?? 1,
          byPositionTotalPages: byPositionData?.meta?.total_pages ?? 1,
          byPositionLoadingMore: false,
          overtimeByTeamMemberType: byStatusData
            ? mapOvertimeByEmploymentStatus(byStatusData.weekly_overtime_by_employment_status ?? [])
            : null,
          overtimeTrend: trendData
            ? mapOvertimeTrendBuckets(trendData.weekly_overtime_trend ?? [])
            : null,
          trendLoading: false,
          overtimeIndividuals: driversData
            ? mapWeeklyOvertimeDrivers(driversData.weekly_overtime_drivers ?? [])
            : null,
          individualsPage: driversData?.meta?.current_page ?? 1,
          individualsTotalPages: driversData?.meta?.total_pages ?? 1,
          individualsLoading: false,
          isLoading: false,
          error: null,
        }));
      });

      return () => {
        fetchId++;
      };
    },
    fetchIndividualsPage: (page) => {
      if (!driversBaseQuery) return;
      const currentFetchId = fetchId;
      const currentDriversFetchId = ++driversFetchId;
      set({ individualsLoading: true });

      createDashboardsRequest<WeeklyOvertimeDriversResponse>({
        url: DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_DRIVERS(),
        method: 'GET',
        query: { ...driversBaseQuery, page },
      })
        .then(({ data }) => {
          if (currentFetchId !== fetchId || currentDriversFetchId !== driversFetchId) return;
          set({
            overtimeIndividuals: mapWeeklyOvertimeDrivers(data.weekly_overtime_drivers ?? []),
            individualsPage: data.meta?.current_page ?? page,
            individualsTotalPages: data.meta?.total_pages ?? 1,
            individualsLoading: false,
          });
        })
        .catch(() => {
          if (currentFetchId !== fetchId || currentDriversFetchId !== driversFetchId) return;
          set({ individualsLoading: false });
        });
    },
    loadMoreByPosition: () => {
      const { byPositionLoadingMore, byPositionPage, byPositionTotalPages } = get();
      if (byPositionLoadingMore || byPositionPage >= byPositionTotalPages || !byPositionBaseQuery) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = byPositionPage + 1;
      set({ byPositionLoadingMore: true });

      fetchDashboardPage<WeeklyOvertimeByPositionResponse, WeeklyOvertimeByPositionResponseItem>({
        url: DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_BY_POSITION(),
        query: byPositionBaseQuery,
        page: nextPage,
        selectItems: (body) => body.weekly_overtime_by_position ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            overtimeByPosition: [
              ...(state.overtimeByPosition ?? []),
              ...mapOvertimeByPosition(items),
            ],
            byPositionPage: meta?.current_page ?? nextPage,
            byPositionTotalPages: meta?.total_pages ?? state.byPositionTotalPages,
            byPositionLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ byPositionLoadingMore: false });
        });
    },
    setTrendPeriods: (periods) => {
      set({ trendPeriods: periods });
      if (!trendBaseParams) return;
      const currentFetchId = fetchId;
      const currentTrendFetchId = ++trendFetchId;
      const params = trendBaseParams;
      set({ trendLoading: true });

      createDashboardsRequest<WeeklyOvertimeTrendResponse>({
        url: DASHBOARDS_OVERTIME_WEEKLY_OVERTIME_TREND(),
        method: 'GET',
        query: buildWeeklyOvertimeTrendQuery(
          params.dateRange,
          params.locationIds,
          params.positions,
          params.employmentTypes,
          periods,
        ),
      })
        .then(({ data }) => {
          if (currentFetchId !== fetchId || currentTrendFetchId !== trendFetchId) return;
          set({
            overtimeTrend: mapOvertimeTrendBuckets(data.weekly_overtime_trend ?? []),
            trendLoading: false,
          });
        })
        .catch(() => {
          if (currentFetchId !== fetchId || currentTrendFetchId !== trendFetchId) return;
          set({ trendLoading: false });
        });
    },
    setData: (data) => set((state) => ({ ...state, ...data })),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    reset: () => {
      fetchId = 0;
      byPositionBaseQuery = null;
      driversFetchId++;
      driversBaseQuery = null;
      trendFetchId++;
      trendBaseParams = null;
      set(INITIAL_STATE);
    },
  })),
);

export const useOvertimeDashboardStore = overtimeDashboardStore;
